import { createFileRoute, Link, useParams, useNavigate } from '@tanstack/react-router'
import { lazy, Suspense, useState, useEffect, useRef, useDeferredValue, useMemo } from 'react'
import { useQueryClient, useSuspenseInfiniteQuery, useSuspenseQuery } from '@tanstack/react-query'
import { useLingui } from '@lingui/react/macro'
import { PlusIcon } from 'lucide-react'
import { trpc } from '@/main'
import { useIsMobile } from '@/hooks/use-mobile'
import { useHydrated } from '@/hooks/use-hydrated'
import { useSearchParamDialog } from '@/hooks/use-search-param-dialog'
import { useRootSearch } from '@/hooks/use-root-search'
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll'
import { usePermissions } from '@/hooks/use-permissions'
import { Button } from '@/components/ui/button'
import { SearchInput } from '@/components/ui/search-input'
import { SectionLabel } from '@/components/custom/section-label'
import { NAVIGATION_ICONS } from './-components/shared/navigation-config'
import { ListPanelBodySkeleton } from './-components/app-layout-skeleton'
import { ListPanePending } from './-components/shared/list-pane-pending'
import { PageLayout, StickyPanel, ScrollableContent, ScrollableArea, SlideInPanel } from './-components/shared/layout'
import { AnimatedOutlet } from './-components/animated-outlet'
import {
  CollapsiblePanelProvider,
  CollapsiblePanel,
  CollapsiblePanelContent,
  CollapsedPanelView,
  CollapsedPanelHeader,
  CollapsedPanelItems,
  CollapsedPanelLinkItem,
} from './-components/shared/collapsible-panel'
import { ScrollableList } from './-components/shared/scrollable-list'
import { SelectionHint } from './-components/shared/collapsible'
import { PanelEmptyState } from './-components/shared/panel-empty-state'
import { CompletionIndicator } from './-components/shared/completion-indicator'
import { EmployeeItem } from './-components/employees/employee-item'
import { PendingInvitationItem } from './-components/employees/pending-invitation-item'
import { ImportExportMenu } from './-components/employees/import-export-menu'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'

const EmployeeForm = lazy(() =>
  import('./-components/companies/employee-form').then((m) => ({ default: m.EmployeeForm })),
)

const SelfAddGateDialog = lazy(() =>
  import('./-components/companies/self-add-gate-dialog').then((m) => ({ default: m.SelfAddGateDialog })),
)

const CRUMB_LINK_CLASS = 'text-foreground hover:text-foreground/70 text-base font-semibold sm:text-xl'

export const Route = createFileRoute('/_app/employees')({
  loaderDeps: ({ search }) => ({ type: search.type }),
  loader: ({ context, deps }) => {
    context.queryClient.prefetchInfiniteQuery(
      trpc.employees.getEmployeesInfinite.infiniteQueryOptions(
        { q: undefined, type: deps.type },
        { getNextPageParam: (lastPage) => lastPage.nextCursor },
      ),
    )
    context.queryClient.prefetchQuery(trpc.employees.getEmployeeInvitations.queryOptions())
  },
  component: RouteComponent,
  pendingComponent: ListPanePending,
})

function RouteComponent() {
  const { t } = useLingui()
  // Employees access is the `employees:read` permission (see usePermissions). The nav link is hidden for
  // anyone without it, but a direct URL hit would otherwise reach the panel's getEmployeesInfinite suspense
  // query and hard-crash on the backend's INSUFFICIENT_PERMISSIONS — so degrade to a no-access state here
  // (mirrors orgs.tsx).
  const canAccessEmployees = usePermissions().can('employees:read')

  return (
    <div id="report" className="@container flex h-full flex-col">
      <PageLayout
        icon={NAVIGATION_ICONS.employees}
        title={t`Employees`}
        breadcrumbsSlot={canAccessEmployees ? <EmployeesBreadcrumbs /> : null}
      >
        {canAccessEmployees ? (
          <EmployeesLayout />
        ) : (
          <ScrollableContent>
            <ScrollableArea>
              <PanelEmptyState
                icon={<NAVIGATION_ICONS.employees className="text-muted-foreground size-5" />}
                title={t`No access`}
                description={t`You don't have permission to access this section`}
              />
            </ScrollableArea>
          </ScrollableContent>
        )}
      </PageLayout>
    </div>
  )
}

function EmployeesLayout() {
  // Gate the viewport-driven auto-collapse on hydration: the panel body hydrates inside a Suspense
  // boundary, so flipping collapse from isMobile mid-hydration races that boundary and mismatches.
  const hydrated = useHydrated()
  const isMobile = useIsMobile() && hydrated
  const { employeeId } = useParams({ strict: false })
  const hasEmployeeSelected = !!employeeId

  const employeeDialog = useSearchParamDialog('editEmployee')
  const editEmployeeId = typeof employeeDialog.value === 'number' ? employeeDialog.value : undefined

  // Admin self-add gate (Model 1): until the company has added ITSELF as an employee, ANY "+ Add" (it
  // navigates to editEmployee=new) opens the self-add gate instead of the normal new-employee form —
  // gating at the render site, so every trigger (toolbar, empty state, collapsed mini) is covered with
  // no bypass. getUser is refreshed on CLOSE (not on add) so the dialog never flips type mid-flow.
  const queryClient = useQueryClient()
  const { data: user } = useSuspenseQuery(trpc.auth.getUser.queryOptions())
  const showSelfAddGate = user?.type === 'company' && user.adminEmployeeId == null && editEmployeeId === undefined
  // Self-add is two steps: the gate captures the admin's email, then the full form opens prefilled with
  // it (admin-self mode). `selfAddEmail` holds the captured email between the two — null = still on the
  // gate. Reset on close so the next "+ Add" starts from the gate again.
  const [selfAddEmail, setSelfAddEmail] = useState<string | null>(null)
  const closeEmployeeDialog = (open: boolean) => {
    if (!open) {
      setSelfAddEmail(null)
      // Refresh getUser on close so a fresh self-add flips the gate off (and arms the conmutador) next time.
      void queryClient.invalidateQueries({ queryKey: trpc.auth.getUser.queryKey() })
    }
    employeeDialog.onOpenChange(open)
  }

  return (
    <CollapsiblePanelProvider defaultCollapsed={isMobile && hasEmployeeSelected}>
      <Suspense fallback={null}>
        {employeeDialog.mounted &&
          (showSelfAddGate && selfAddEmail == null ? (
            <SelfAddGateDialog
              open={employeeDialog.open}
              onOpenChange={closeEmployeeDialog}
              // Captured a valid email: switch to the full form (admin-self mode) prefilled with it.
              onContinue={setSelfAddEmail}
            />
          ) : (
            <EmployeeForm
              employeeId={editEmployeeId}
              open={employeeDialog.open}
              onOpenChange={closeEmployeeDialog}
              adminSelf={showSelfAddGate}
              prefill={showSelfAddGate && selfAddEmail != null ? { email: selfAddEmail } : undefined}
            />
          ))}
      </Suspense>

      <div className="flex w-full overflow-hidden" style={{ containerType: 'inline-size' }}>
        {/* Left Panel - Always flex-1, shrinks as report grows */}
        <StickyPanel className="flex-1 print:hidden">
          <CollapsiblePanel
            expandedWidth="100%"
            disableTransition
            className={!hasEmployeeSelected ? 'border-r-0' : undefined}
          >
            <Suspense fallback={<ListPanelBodySkeleton />}>
              <EmployeesPanel />
            </Suspense>
          </CollapsiblePanel>
        </StickyPanel>

        {/* Right Panel - Always in DOM, width transitions from 0 to target */}
        <SlideInPanel isOpen={hasEmployeeSelected} innerClassName="flex flex-col" className="md:[--sip-left:450px]">
          <ScrollableArea>{hasEmployeeSelected && <AnimatedOutlet className="h-full" />}</ScrollableArea>
        </SlideInPanel>
      </div>
    </CollapsiblePanelProvider>
  )
}

function EmployeesBreadcrumbs() {
  const { t } = useLingui()
  const { employeeId } = useParams({ strict: false })
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild className={CRUMB_LINK_CLASS}>
            <Link
              to="/employees"
              search={(prev) => ({ ...prev, first: undefined, second: undefined, selected: undefined })}
            >
              {t`Employees`}
            </Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        {employeeId && (
          <Suspense fallback={null}>
            <EmployeeDetailCrumb employeeId={employeeId} />
          </Suspense>
        )}
      </BreadcrumbList>
    </Breadcrumb>
  )
}

function EmployeeDetailCrumb(props: { employeeId: string }) {
  const { data: employee } = useSuspenseQuery(trpc.employees.getEmployee.queryOptions(Number(props.employeeId)))
  const employeeName = employee?.employee_name ?? employee?.name
  if (!employeeName) return null
  return (
    <>
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbLink asChild className={CRUMB_LINK_CLASS}>
          <Link
            to="/employees/$employeeId/report"
            params={{ employeeId: props.employeeId }}
            search={(prev) => ({ ...prev, first: undefined, second: undefined, selected: undefined })}
          >
            {employeeName}
          </Link>
        </BreadcrumbLink>
      </BreadcrumbItem>
    </>
  )
}

function EmployeesPanel() {
  const { t } = useLingui()
  const { employeeId } = useParams({ strict: false })
  const { q: queryFromSearch, type } = useRootSearch()
  const navigate = useNavigate()

  const employeesInput = useMemo(() => ({ q: queryFromSearch || undefined, type }), [queryFromSearch, type])
  const deferredEmployeesInput = useDeferredValue(employeesInput)
  const { data, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } = useSuspenseInfiniteQuery(
    trpc.employees.getEmployeesInfinite.infiniteQueryOptions(deferredEmployeesInput, {
      getNextPageParam: (lastPage) => lastPage.nextCursor,
    }),
  )
  // True while the deferred input lags the live one (a change is in flight) — keeps the fetching
  // indicator visible during the keep-previous transition.
  const isEmployeesFetching = isFetching || deferredEmployeesInput !== employeesInput

  const { loadMoreRef } = useInfiniteScroll({
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    fetchNextPage,
  })

  // Flatten all pages into a single list
  const allEmployees = data.pages.flatMap((page) => page.list)

  // Separate added employees from other profiles
  const addedEmployees = allEmployees.filter((e) => e.added)

  const employeeTypeLabels: Record<string, string> = {
    administrator: t`Administrator`,
    authorized: t`Authorized`,
    collaborator: t`Collaborator`,
  }

  return (
    <>
      {/* Collapsed view - show mini avatars */}
      <CollapsedPanelView>
        <CollapsedPanelHeader>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t`Add employee`}
            className="size-8"
            onClick={() => navigate({ to: '.', search: (prev) => ({ ...prev, editEmployee: 'new' }) })}
          >
            <PlusIcon className="size-4" />
          </Button>
        </CollapsedPanelHeader>
        <CollapsedPanelItems
          isLoading={false}
          isFetching={isEmployeesFetching}
          isFetchingMore={isFetchingNextPage}
          loadMoreRef={loadMoreRef}
        >
          {addedEmployees.map((employee) => {
            const hasSkills = employee.testPassed
            const hasValues = employee.testPassedValues
            const hasTraits = employee.testPassedPersonality

            return (
              <CollapsedPanelLinkItem
                key={employee.id}
                name={employee.employee_name ?? employee.name ?? ''}
                imageUrl={employee.image}
                type="employee"
                isSelected={employeeId === employee.id.toString()}
                tooltipContent={
                  <div className="flex flex-col gap-1.5">
                    <p className="font-medium">{employee.employee_name ?? employee.name}</p>
                    <div className="text-muted-foreground flex items-center gap-2.5 text-xs">
                      <CompletionIndicator skills={hasSkills} values={hasValues} traits={hasTraits} expanded />
                      {employee.employeeType && <span>{employeeTypeLabels[employee.employeeType]}</span>}
                    </div>
                  </div>
                }
              >
                {(content) => (
                  <Link
                    to="/employees/$employeeId/report"
                    params={{ employeeId: employee.id.toString() }}
                    search={(prev) => ({
                      ...prev,
                      first: { type: 'employee', id: employee.id },
                      second: undefined,
                      selected: undefined,
                    })}
                  >
                    {content}
                  </Link>
                )}
              </CollapsedPanelLinkItem>
            )
          })}
        </CollapsedPanelItems>
      </CollapsedPanelView>

      {/* Expanded view */}
      <CollapsiblePanelContent>
        <EmployeesPanelContent />
      </CollapsiblePanelContent>
    </>
  )
}

function EmployeesPanelContent() {
  const { t } = useLingui()
  const { employeeId } = useParams({ strict: false })
  const navigate = useNavigate()
  const { q: queryFromSearch, type } = useRootSearch()
  const [localQuery, setLocalQuery] = useState(queryFromSearch)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  // All "+ Add" triggers just navigate to editEmployee=new; the render site decides whether to show the
  // self-add gate or the normal form (so the gate has no bypass).
  const openAddEmployee = () => navigate({ to: '.', search: (prev) => ({ ...prev, editEmployee: 'new' }) })
  // Authorized employees view the company read-only (Block C): hide the add control (the backend also
  // rejects the write). Companies + administrators can add.
  const canAddEmployees = usePermissions().can('employees:write')

  const testTypeLabels: Record<string, string> = {
    competences: t`Competences`,
    values: t`Values`,
    personality: t`Personality`,
    global: t`Global`,
  }
  const currentTestLabel = testTypeLabels[type] ?? ''

  // Sync local query with URL search param
  useEffect(() => {
    setLocalQuery(queryFromSearch)
  }, [queryFromSearch])

  // `type` is sent to the resolver so each row carries a pre-selected `hasCurrentTest` (the
  // per-type test-completion flag), replacing the old client-side ternary.
  const employeesInput = useMemo(() => ({ q: queryFromSearch || undefined, type }), [queryFromSearch, type])
  const deferredEmployeesInput = useDeferredValue(employeesInput)
  const { data, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } = useSuspenseInfiniteQuery(
    trpc.employees.getEmployeesInfinite.infiniteQueryOptions(deferredEmployeesInput, {
      getNextPageParam: (lastPage) => lastPage.nextCursor,
    }),
  )
  // True while the deferred input lags the live one (a change is in flight) — keeps the fetching
  // indicator visible during the keep-previous transition.
  const isEmployeesFetching = isFetching || deferredEmployeesInput !== employeesInput

  const { loadMoreRef } = useInfiniteScroll({
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    fetchNextPage,
  })

  // Flatten all pages into a single list
  const allEmployees = data.pages.flatMap((page) => page.list)

  // Consent requests still awaiting the person's answer (email matched an existing account).
  const { data: invitations } = useSuspenseQuery(trpc.employees.getEmployeeInvitations.queryOptions())

  // Get total count from first page
  const totalCount = data.pages[0]?.pagination.count ?? 0

  // Derive "is a search active" from the DEFERRED input (the `q` that produced the list currently on
  // screen), NOT the live URL `q`. Otherwise clearing the search flips this to false while the stale
  // search results are still shown, briefly rendering the cold empty state before the full list loads.
  const isSearching = !!deferredEmployeesInput.q
  const showOnlyEmptyState = allEmployees.length === 0 && invitations.length === 0 && !isSearching

  const employeeTypeText: Record<string, string> = {
    administrator: t`Administrator`,
    authorized: t`Authorized`,
    collaborator: t`Collaborator`,
  }

  // Handle search input with debounce
  const handleSearchChange = (value: string) => {
    setLocalQuery(value)

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      navigate({ to: '.', search: (prev) => ({ ...prev, q: value || undefined }), replace: true })
    }, 300)
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const pendingInvitationsSection = invitations.length > 0 && !isSearching && (
    <>
      <SectionLabel className="px-2">{t`Pending consent`}</SectionLabel>
      {invitations.map((invitation) => (
        <PendingInvitationItem key={invitation.id} invitation={invitation} />
      ))}
    </>
  )

  return (
    <>
      {!showOnlyEmptyState && (
        <>
          {/* Search and create button */}
          <div className="flex h-23 items-center gap-2 border-b px-4 py-3">
            <SearchInput value={localQuery} onChange={handleSearchChange} placeholder={t`Search employees...`} />
            {canAddEmployees && <ImportExportMenu variant="sidebar" />}
            {canAddEmployees && (
              <Button variant="secondary" onClick={openAddEmployee}>
                <PlusIcon className="size-4" />
                {t`Add`}
              </Button>
            )}
          </div>
        </>
      )}

      {/* Employee List */}
      <ScrollableList
        isFetching={isEmployeesFetching}
        loadMoreRef={loadMoreRef}
        isFetchingMore={isFetchingNextPage}
        isEmpty={allEmployees.length === 0 && invitations.length === 0}
        hint={
          allEmployees.length > 0 && (
            <SelectionHint visible={!employeeId} text={t`Select an employee to view their report`} />
          )
        }
        emptyState={
          <PanelEmptyState
            noResults={allEmployees.length === 0 && isSearching}
            icon={<NAVIGATION_ICONS.employees className="text-muted-foreground size-5" />}
            title={t`No employees yet`}
            description={t`Add employees to get started`}
            action={
              canAddEmployees ? (
                <Button variant="secondary" onClick={openAddEmployee}>
                  <PlusIcon className="size-4" />
                  {t`Add employee`}
                </Button>
              ) : undefined
            }
          />
        }
      >
        {pendingInvitationsSection}
        <SectionLabel className="px-2">
          {totalCount === 1 ? t`${totalCount} employee` : t`${totalCount} employees`}
        </SectionLabel>
        {allEmployees.map((employee) => {
          const isItemSelected = employeeId === employee.id.toString()
          const hasTest = employee.hasCurrentTest
          const isItemDisabled = !hasTest && !isItemSelected
          return (
            <EmployeeItem
              key={employee.id}
              employee={employee}
              isSelected={isItemSelected}
              hasCurrentTest={hasTest}
              currentTestLabel={currentTestLabel}
              employeeTypeText={employee.employeeType ? employeeTypeText[employee.employeeType] : undefined}
              variant="added"
            >
              {(content) =>
                isItemDisabled ? (
                  <div>{content}</div>
                ) : (
                  <Link
                    to="/employees/$employeeId/report"
                    params={{ employeeId: employee.id.toString() }}
                    search={(prev) => ({
                      ...prev,
                      first: { type: 'employee', id: employee.id },
                      second: undefined,
                      selected: undefined,
                    })}
                  >
                    {content}
                  </Link>
                )
              }
            </EmployeeItem>
          )
        })}
      </ScrollableList>
    </>
  )
}
